import { useEffect, useRef, useState } from 'react';

export default function UXJourney() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const journeySteps = [
    {
      id: 1,
      year: '2019',
      title: 'The Beginning',
      icon: 'ri-lightbulb-line',
      description: 'I first came across UI/UX during my Computer Science Engineering studies. It immediately caught my attention because it combined creativity with logic. For the first time, I felt like this was something I could truly enjoy building a career in.',
    },
    {
      id: 2,
      year: '2020',
      title: 'Exploring the Field',
      icon: 'ri-search-line',
      description: 'I spent a lot of time researching the field — how it works, the tools designers use, the career scope, and what the day-to-day looks like. The more I learned, the more confident I became that this was the right path for me. Everything about the field felt exciting and full of opportunities.',
    },
    {
      id: 3,
      year: '2020',
      title: 'Learning the Basics',
      icon: 'ri-palette-line',
      description: 'I started learning Figma by watching YouTube tutorials and practicing on my own. I also completed certifications from Google, Accenture, Georgia Tech, and others, which helped me understand design thinking and user-centered design. This phase helped me build a strong foundation and understand how real products are designed.',
    },
    {
      id: 4,
      year: 'Mid 2020',
      title: 'Practicing & Sharing',
      icon: 'ri-rocket-line',
      description: 'I began creating simple UI screens and posting them on Instagram. Getting feedback from other designers helped me improve quickly and understand what good design looks like. This was when I learned the importance of iteration and staying open to feedback.',
    },
    {
      id: 5,
      year: '2021',
      title: 'First Step into the Industry',
      icon: 'ri-briefcase-line',
      description: 'I got my first 6-month internship, where I worked on real projects for the first time. Seeing my designs being used was a big moment for me — it made everything feel real. This experience confirmed that UI/UX is what I wanted to focus on full-time.',
    },
    {
      id: 6,
      year: '2023',
      title: 'Moving to Canada',
      icon: 'ri-plane-line',
      description: 'I moved to Toronto to study UX Design at Humber Polytechnic. Here, I learned advanced UX methods, design systems, and accessibility, and worked with students from different backgrounds. This helped me grow not just as a designer, but also in how I think about solving problems.',
    },
    {
      id: 7,
      year: '2024',
      title: 'Getting More Hands-On',
      icon: 'ri-puzzle-line',
      description: 'I completed another internship, working on internal tools and improving user workflows. This helped me understand how to simplify complex tasks and design solutions that actually help people work better. It was a great opportunity to apply everything I had learned so far.',
    },
    {
      id: 8,
      year: 'Today',
      title: 'Still Growing',
      icon: 'ri-flashlight-line',
      description: "I'm still learning, exploring new ideas, and improving my skills every day. Every project helps me understand design better and pushes me to grow. I'm excited about where this journey will take me next.",
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / 10;
    const rotateY = (centerX - x) / 10;

    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`;
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = e.currentTarget;
    card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)';
  };

  return (
    <section
      id="about-me"
      ref={sectionRef}
      className="relative py-32 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className={`text-center mb-20 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'}`}>
          <span className="inline-block px-4 py-2 text-white text-sm font-bold rounded-full mb-4" style={{ backgroundColor: '#6b3d52' }}>My Story</span>
          <h2 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            My Journey Into UI/UX Design
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            From curiosity to career — here's how I found my path in design
          </p>
        </div>

        {/* Journey Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-1 transform -translate-x-1/2" style={{ backgroundColor: '#fecdd3' }}></div>

          {/* Journey Cards */}
          <div className="space-y-24">
            {journeySteps.map((step, index) => {
              const isLeft = index % 2 === 0;
              return (
                <div
                  key={step.id}
                  className={`flex items-center gap-8 ${isLeft ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  {/* Card */}
                  <div className={`flex-1 transition-all duration-700 ${
                    isVisible 
                      ? 'opacity-100 translate-x-0' 
                      : isLeft 
                        ? 'opacity-0 translate-x-20' 
                        : 'opacity-0 -translate-x-20'
                  }`}
                  style={{ transitionDelay: `${index * 200}ms` }}>
                    <div 
                      className="bg-white border-2 border-slate-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 cursor-pointer"
                      style={{ 
                        transformStyle: 'preserve-3d',
                        transition: 'transform 0.1s ease-out'
                      }}
                      onMouseMove={handleMouseMove}
                      onMouseLeave={handleMouseLeave}
                      onMouseEnter={(e) => e.currentTarget.style.borderColor = '#6b3d52'}
                      onMouseOut={(e) => e.currentTarget.style.borderColor = '#e2e8f0'}
                    >
                      {/* Year Badge */}
                      <div className="inline-flex items-center gap-2 rounded-lg px-4 py-2 mb-6" style={{ backgroundColor: '#6b3d52' }}>
                        <span className="text-sm font-bold text-white">{step.year}</span>
                      </div>

                      {/* Icon */}
                      <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-slate-100 border-2 border-slate-200 mb-6 inline-flex">
                        <i className={`${step.icon} text-3xl`} style={{ color: '#6b3d52' }}></i>
                      </div>

                      {/* Title */}
                      <h3 className="text-2xl font-bold text-slate-900 mb-4">{step.title}</h3>

                      {/* Description */}
                      <p className="text-base text-slate-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>

                  {/* Timeline Dot */}
                  <div className="absolute left-1/2 top-8 w-6 h-6 border-4 border-white rounded-full transform -translate-x-1/2 shadow-lg z-10" style={{ backgroundColor: '#6b3d52' }}></div>

                  {/* Spacer */}
                  <div className="flex-1"></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
